import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "./api";

const AuthContext = createContext(null);

// Auth is now driven entirely by the httpOnly `access_token` cookie set by the backend.
// We never read or write tokens in JS — `/api/auth/me` is the source of truth on every page load.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = signed-in
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    // Returning from OAuth — AuthCallback exchanges the session_id first; skip the probe.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 on initial probe is expected for guests — only log unexpected failures.
      if (e?.response?.status !== 401) console.error("auth refresh failed:", e);
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Iter 68 — 2FA gating: backend returns `{requires_2fa, twofa_challenge_token}`
      // instead of the user object when the account has TOTP enabled. Surface
      // that to the caller so the Login page can show the 6-digit code prompt.
      if (data?.requires_2fa) {
        return { ok: true, requires2fa: true, challengeToken: data.twofa_challenge_token, expiresIn: data.expires_in_seconds };
      }
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const verify2fa = async (challengeToken, code, rememberDevice = false) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", {
        challenge_token: challengeToken,
        code,
        remember_device: rememberDevice,
      });
      setUser(data.user);
      return { ok: true, user: data.user, deviceTrusted: !!data.device_trusted };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("logout request failed:", e); }
    setUser(false);
    // Iter 69 — clear vendor-scope state on logout so the next session
    // starts with a clean Browse view.
    try {
      window.localStorage.removeItem("rentalcar:source_vendor_id");
      window.localStorage.removeItem("rentalcar:source_vendor_name");
      window.dispatchEvent(new CustomEvent("rentalcar:source-vendor-changed"));
    } catch (_e) { /* ignore */ }
  };

  // Exchange Emergent OAuth session_id → server-side cookie. Used by AuthCallback.
  const loginWithGoogleSession = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      setUser(data.user);
      setLoading(false);
      return { ok: true, user: data.user };
    } catch (e) {
      setLoading(false);
      return { ok: false, error: formatApiError(e) };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verify2fa, register, logout, refresh, loginWithGoogleSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
